import React, { useEffect, useState } from "react";
import { ChevronDown, LogOut, Mic, Video } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { API } from "@/lib/api";
import { resolveProfileAvatarUrl } from "@/lib/profileAvatar.mjs";
import { roleLabel } from "@/lib/roleLabels";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLocalization } from "@/localization/LocalizationContext";

export default function PlatformProfileMenu({ user, onLogout }) {
  const nav = useNavigate();
  const { t } = useLocalization();
  const initials = ((user?.display_name || user?.name || "О К").trim().split(/\s+/).map((part) => part[0]).slice(0, 2).join("") || "О").toUpperCase();
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const avatarSrc = resolveProfileAvatarUrl(user?.avatar, API);

  useEffect(() => { setAvatarFailed(false); }, [user?.avatar]);

  return (
    <div className="relative shrink-0" data-testid="platform-profile">
      <button type="button" onClick={() => setMenuOpen((value) => !value)}
              className="flex h-10 items-center gap-2.5 rounded-xl bg-white pl-1 pr-3 transition hover:bg-white"
              style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
              aria-expanded={menuOpen}
              data-testid="mo-topbar-avatar">
        <div className="grid h-8 w-8 place-items-center overflow-hidden rounded-xl text-[12px]"
             style={{ background: "linear-gradient(135deg, #EADFCF 0%, #C7A574 100%)", color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif", fontSize: 14 }}
             data-testid="mo-topbar-avatar-slot">
          {avatarSrc && !avatarFailed && <img src={avatarSrc} alt="" className="hidden h-full w-full rounded-xl object-cover lg:block" onError={() => setAvatarFailed(true)} data-testid="mo-topbar-avatar-image" />}
          <span className={avatarSrc && !avatarFailed ? "lg:hidden" : ""} data-testid="mo-topbar-avatar-initials">{initials}</span>
        </div>
        <div className="hidden text-left lg:block">
          <div className="text-[12.5px] leading-none text-[#2A1D14]">
            {user?.display_name || user?.name || "Пользователь"}
          </div>
          <div className="mt-0.5 text-[10px] text-[#8B6F4B]">{roleLabel(user?.role)}</div>
        </div>
        <ChevronDown size={13} color="#8B6F4B" strokeWidth={1.8} />
      </button>
      {menuOpen && (
        <div className="absolute right-0 top-12 z-30 w-56 rounded-xl bg-white py-1.5"
             style={{ boxShadow: "0 12px 28px -12px rgba(58,43,31,0.24), inset 0 0 0 1px #EFE4D0" }}
             data-testid="mo-topbar-menu">
          <LanguageSwitcher />
          {user?.role === "owner" && <button type="button" onClick={() => { setMenuOpen(false); nav("/owner/voice-foundation"); }} className="mx-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-[#4A3728] hover:bg-black/5" data-testid="voice-foundation-proof-link"><Mic size={14} strokeWidth={1.6} />{t("voice.proof.menu")}</button>}
          {user?.role === "owner" && <button type="button" onClick={() => { setMenuOpen(false); nav("/owner/private-video-foundation"); }} className="mx-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-[#4A3728] hover:bg-black/5" data-testid="private-video-foundation-proof-link"><Video size={14} strokeWidth={1.6} />Private Video proof</button>}
          <div className="mx-3 h-px bg-[#EFE4D0]" />
          <button type="button" onClick={onLogout}
                  className="mx-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-[13px] text-[#4A3728] hover:bg-black/5"
                  data-testid="mo-topbar-logout">
            <LogOut size={14} strokeWidth={1.6} /> {t("shared.signOut")}
          </button>
        </div>
      )}
    </div>
  );
}
